import React, { useMemo } from "react"

interface OutlinedTextBaseProps {
    text: string
    strokeColor?: string
    strokeWidth?: number
    fillColor?: string
    fontSize?: number | string
    fontFamily?: string
    style?: React.CSSProperties
    className?: string
    // 新增属性：是否启用环形文字、环形半径
    circular?: boolean
    circleRadius?: number
    // 新增属性：渐变配置 { from: string, to: string }
    fillGradient?: { from: string; to: string }
    // 新增属性：双层边框 - 外层边框颜色和宽度
    outerStrokeColor?: string
    outerStrokeWidth?: number
}

interface OutlinedTextWithWrapProps extends OutlinedTextBaseProps {
    /** 是否启用换行 */
    wrap: true
    /** 最大宽度，超过此宽度时自动换行 */
    maxWidth: number
}

interface OutlinedTextWithoutWrapProps extends OutlinedTextBaseProps {
    /** 是否启用换行 */
    wrap?: false
    /** 最大宽度，超过此宽度时自动换行 */
    maxWidth?: never
}

export type OutlinedTextProps = OutlinedTextWithWrapProps | OutlinedTextWithoutWrapProps

const OutlinedText: React.FC<OutlinedTextProps> = ({
    text,
    strokeColor = "#000",
    strokeWidth = 2,
    fillColor = "#fff",
    fontSize = 20,
    fontFamily = "SourceHanSansJP-VF",
    style = {},
    className = "",
    circular = false,
    circleRadius,
    fillGradient,
    wrap = false,
    maxWidth,
    outerStrokeColor,
    outerStrokeWidth,
}) => {
    const fontSizeNum = Number(fontSize)
    const strokeWidthNum = Number(strokeWidth)
    const outerStrokeWidthNum = outerStrokeWidth ? Number(outerStrokeWidth) : 0
    const hasDoubleStroke = !!outerStrokeColor && outerStrokeWidthNum > 0
    // padding 用于给 stroke 留出空间
    // SVG 的 stroke 是向外扩展的，向外扩展 strokeWidth/2
    // 所以 padding 需要 strokeWidth 来容纳完整的 stroke
    // 对于双层边框，padding 应该基于外层边框（因为外层边框在最外层）
    // 对于单层边框，padding 基于内层边框即可
    const paddingLeft = (hasDoubleStroke ? outerStrokeWidthNum : strokeWidthNum) + 2
    const paddingRight = hasDoubleStroke ? outerStrokeWidthNum : strokeWidthNum // 右侧减少 padding，让组件可以紧邻
    const paddingTop = hasDoubleStroke ? outerStrokeWidthNum : strokeWidthNum
    const paddingBottom = hasDoubleStroke ? outerStrokeWidthNum : strokeWidthNum
    // 宽度计算：所有字符都按 fontSize 计算，确保宽度足够，避免被裁剪
    const textLength = String(text).length
    const estimatedWidth = textLength * fontSizeNum
    // 宽度 = 文本估算宽度 + 左右 padding
    // padding 已经包含了 stroke 向外扩展的空间，不需要额外增加
    const width = estimatedWidth + paddingLeft + paddingRight
    // 高度 = 字体大小 + 上下 padding
    // padding 已经包含了 stroke 向外扩展的空间，不需要额外增加
    const height = fontSizeNum + paddingTop + paddingBottom
    
    // 将像素值转换为rem单位，使其能够响应Resize组件的根字体大小变化
    // 获取当前的根字体大小，用于正确的rem转换
    const currentRootFontSize = typeof window !== "undefined" 
        ? Number(window.getComputedStyle(document.documentElement).fontSize.replace("px", "")) || 16
        : 16
    const baseFontSize = 16 // 设计稿基准字体大小

    // 检查是否需要使用渐变
    const useGradient = !!fillGradient
    const gradientId = useMemo(
        () => `text-gradient-${Math.random().toString(36).substr(2, 9)}`,
        []
    )

    // 如果启用了换行，计算文本行
    const { lines, actualWidth, actualHeight, textCenterX } = useMemo(() => {
        if (!wrap || !maxWidth) {
            return {
                lines: [text],
                actualWidth: width,
                actualHeight: height,
                textCenterX: width / 2, // 未启用换行时，文本中心也是容器中心
            }
        }
        // maxWidth 是设计稿像素值，需要根据当前缩放比例调整
        // 例如：如果根字体从 16px 缩放到 12px，那么 maxWidth 也应该相应缩小
        const scaledMaxWidth = maxWidth * (currentRootFontSize / baseFontSize)
        const availableWidth = scaledMaxWidth - paddingLeft - paddingRight
        const resultLines: string[] = []
        let currentLine = ""

        // 创建一个临时的SVG text元素来测量文本宽度
        const svgNS = "http://www.w3.org/2000/svg"
        const tempSvg = document.createElementNS(svgNS, "svg")
        const tempText = document.createElementNS(svgNS, "text")
        tempText.setAttribute("font-size", String(fontSizeNum))
        tempText.setAttribute("font-family", fontFamily)
        tempSvg.appendChild(tempText)
        document.body.appendChild(tempSvg)

        // 判断是否为数字字符，用于换行时避免把数字从中折断
        const isDigit = (c: string) => /^\d$/.test(c)

        try {
            for (let i = 0; i < text.length; i++) {
                const char = text[i]

                // 如果当前字符是换行符，直接换行
                if (char === "\n") {
                    if (currentLine) {
                        resultLines.push(currentLine)
                        currentLine = ""
                    }
                    continue
                }

                // 测量当前行加上新字符后的宽度
                tempText.textContent = currentLine + char
                const newWidth = tempText.getComputedTextLength()

                // 如果加上当前字符会超过宽度，先保存当前行
                if (newWidth > availableWidth && currentLine) {
                    // 若当前行末尾是数字且新字符也是数字，则不在数字中间断行，把整段数字移到下一行
                    if (isDigit(char) && isDigit(currentLine[currentLine.length - 1])) {
                        let digitStart = currentLine.length - 1
                        while (digitStart > 0 && isDigit(currentLine[digitStart - 1])) {
                            digitStart--
                        }
                        resultLines.push(currentLine.slice(0, digitStart))
                        currentLine = currentLine.slice(digitStart) + char
                    } else {
                        resultLines.push(currentLine)
                        currentLine = char
                    }
                } else {
                    currentLine += char
                }
            }

            // 添加最后一行
            if (currentLine) {
                resultLines.push(currentLine)
            }
        } finally {
            document.body.removeChild(tempSvg)
        }

        const finalLines = resultLines.length > 0 ? resultLines : [text]
        const lineHeight = fontSizeNum * 1.0 // 减小行间距，从1.2改为1.0
        // 使用缩放后的宽度（实际容器宽度）
        const finalWidth = scaledMaxWidth
        // 计算文本居中的 x 坐标：使用 SVG 容器的中心点
        // 这样文本内容会在整个 SVG 容器中水平居中
        const textCenterX = finalWidth / 2
        // 高度计算：
        // - 第一行从 paddingTop 开始（y坐标，使用 hanging 基线）
        // - 第一行文字高度为 fontSizeNum
        // - 后续每行高度为 lineHeight
        // - 总高度 = paddingTop + fontSizeNum + (lines.length - 1) * lineHeight + paddingBottom
        const finalHeight = paddingTop + fontSizeNum + (finalLines.length - 1) * lineHeight + paddingBottom

        return {
            lines: finalLines,
            actualWidth: finalWidth,
            actualHeight: finalHeight,
            textCenterX, // 返回文本居中的 x 坐标
        }
    }, [text, wrap, maxWidth, fontSizeNum, fontFamily, paddingLeft, paddingRight, paddingTop, paddingBottom, width, height, currentRootFontSize])

    const lineHeight = fontSizeNum * 1.0 // 行高为字体大小的1.0倍（减小行间距）
    const widthRem = actualWidth / baseFontSize
    const heightRem = actualHeight / baseFontSize

    const isIos = (/iPhone/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent))
    
    // 检测浏览器类型，针对不同浏览器使用不同的基线对齐策略
    const isSafari = isIos || (typeof window !== "undefined" && /^((?!chrome|android).)*safari/i.test(navigator.userAgent))
    // Chrome 使用 "middle"，Safari 使用 "central" 来实现严格居中
    // 根据实际测试：Chrome 中 "central" 偏下，Safari 中 "middle" 偏上，所以互换使用
    // 换行模式在 Safari 中也需要特殊处理
    const dominantBaselineValue = wrap && maxWidth 
        ? (isSafari ? "hanging" : "hanging")  // 换行模式都使用 hanging，但通过 dy 调整
        : (isSafari ? "central" : "middle")
    // Safari 中文字仍然偏上，需要添加向下偏移来精确居中
    // 使用 em 单位，相对于字体大小，这样更精确且与字体大小无关
    // 换行模式在 Safari 中也需要向下偏移，需要更大的偏移量
    const firstLineDy = wrap && maxWidth 
        ? (isSafari ? "0.5em" : 0)  // 换行模式在 Safari 中需要更大的偏移量
        : (isSafari ? "0.2em" : 0)

    // 环形文字相关参数
    const defaultRadius =
        Math.max(width, height) / 2 -
        fontSizeNum / 2 -
        Math.max(paddingLeft, paddingRight)
    const radius = circleRadius || defaultRadius
    const centerX = width / 2
    const centerY = height / 2 + radius
    const pathId = useMemo(
        () =>
            `outlined-text-circle-path-${Math.random().toString(36).substr(2, 9)}`,
        []
    )

    if (circular) {
        // 环形文字渲染（不支持换行）
        const circularWidthRem = width / baseFontSize
        const circularHeightRem = height / baseFontSize
        return (
            <svg
                style={{
                    display: "inline-block",
                    verticalAlign: "middle",
                    width: `${circularWidthRem}rem`,
                    height: `${circularHeightRem}rem`,
                    ...style,
                }}
                className={className}
                viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio="xMinYMid"
                xmlns="http://www.w3.org/2000/svg"
            >
                <defs>
                    <path
                        id={pathId}
                        d={`M ${centerX - radius},${centerY} a ${radius},${radius} 0 1,1 ${radius * 2},0 a ${radius},${radius} 0 1,1 -${radius * 2},0`}
                        fill="none"
                    />
                    {useGradient && fillGradient && (
                        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor={fillGradient.from} />
                            <stop offset="100%" stopColor={fillGradient.to} />
                        </linearGradient>
                    )}
                </defs>
                {/* 外层边框（如果启用双层边框） */}
                {hasDoubleStroke && (
                    <text
                        stroke={outerStrokeColor}
                        strokeWidth={outerStrokeWidthNum}
                        fill={"none"}
                        fontSize={fontSizeNum}
                        fontFamily={fontFamily}
                        strokeLinejoin="round"
                        style={{ pointerEvents: "none", userSelect: "none" }}
                    >
                        <textPath
                            xlinkHref={`#${pathId}`}
                            startOffset="25%"
                            textAnchor="middle"
                            dominantBaseline="central"
                        >
                            {text}
                        </textPath>
                    </text>
                )}
                {/* 内层边框 */}
                <text
                    stroke={strokeColor}
                    strokeWidth={strokeWidth}
                    fill={"none"}
                    fontSize={fontSizeNum}
                    fontFamily={fontFamily}
                    strokeLinejoin="round"
                    style={{ pointerEvents: "none", userSelect: "none" }}
                >
                    <textPath
                        xlinkHref={`#${pathId}`}
                        startOffset="25%"
                        textAnchor="middle"
                        dominantBaseline="central"
                    >
                        {text}
                    </textPath>
                </text>
                {/* 填充层 */}
                <text
                    fill={useGradient ? `url(#${gradientId})` : fillColor}
                    fontSize={fontSizeNum}
                    fontFamily={fontFamily}
                    style={{ pointerEvents: "none", userSelect: "none" }}
                >
                    <textPath
                        xlinkHref={`#${pathId}`}
                        startOffset="25%"
                        textAnchor="middle"
                        dominantBaseline="central"
                    >
                        {text}
                    </textPath>
                </text>
            </svg>
        )
    }

    return (
        <svg
            style={{
                display: "inline-block",
                verticalAlign: "middle",
                width: `${widthRem}rem`,
                height: `${heightRem}rem`,
                ...style,
            }}
            className={className}
            viewBox={`0 0 ${actualWidth} ${actualHeight}`}
            preserveAspectRatio={wrap && maxWidth ? "xMidYMid" : "xMinYMid"}
            xmlns="http://www.w3.org/2000/svg"
        >
            {useGradient && fillGradient && (
                <defs>
                    <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor={fillGradient.from} />
                        <stop offset="100%" stopColor={fillGradient.to} />
                    </linearGradient>
                </defs>
            )}
            {/* 外层边框（如果启用双层边框） */}
            {hasDoubleStroke && (
                <text
                    x={textCenterX}
                    y={wrap && maxWidth ? paddingTop : actualHeight / 2}
                    textAnchor="middle"
                    stroke={outerStrokeColor}
                    strokeWidth={outerStrokeWidthNum}
                    fill={"none"}
                    fontSize={fontSizeNum}
                    fontFamily={fontFamily}
                    dominantBaseline={dominantBaselineValue}
                    strokeLinejoin="round"
                    style={{ pointerEvents: "none", userSelect: "none" }}
                >
                    {lines.map((line, index) => {
                        return (
                            <tspan
                                key={`outer-${index}-${line}`}
                                x={textCenterX}
                                dy={index === 0 ? firstLineDy : lineHeight}
                                textAnchor="middle"
                            >
                                {line}
                            </tspan>
                        )
                    })}
                </text>
            )}
            {/* 内层边框 */}
            <text
                x={textCenterX}
                y={wrap && maxWidth ? paddingTop : actualHeight / 2}
                textAnchor="middle"
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                fill={"none"}
                fontSize={fontSizeNum}
                fontFamily={fontFamily}
                dominantBaseline={dominantBaselineValue}
                strokeLinejoin="round"
                style={{ pointerEvents: "none", userSelect: "none" }}
            >
                {lines.map((line, index) => {
                    return (
                        <tspan
                            key={`inner-${index}-${line}`}
                            x={textCenterX}
                            dy={index === 0 ? firstLineDy : lineHeight}
                            textAnchor="middle"
                        >
                            {line}
                        </tspan>
                    )
                })}
            </text>
            {/* 填充层 */}
            <text
                x={textCenterX}
                y={wrap && maxWidth ? paddingTop : actualHeight / 2}
                textAnchor="middle"
                fill={useGradient ? `url(#${gradientId})` : fillColor}
                fontSize={fontSizeNum}
                fontFamily={fontFamily}
                dominantBaseline={dominantBaselineValue}
                style={{ pointerEvents: "none", userSelect: "none" }}
            >
                {lines.map((line, index) => {
                    return (
                        <tspan
                            key={`${index}-${line}`}
                            x={textCenterX}
                            dy={index === 0 ? firstLineDy : lineHeight}
                            textAnchor="middle"
                        >
                            {line}
                        </tspan>
                    )
                })}
            </text>
        </svg>
    )
}

export default OutlinedText
